import React, { memo, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { Box, CircularProgress, Typography } from '@mui/material'
import QuickWidgetDialog from '../../../quickWidgetDialog/quickWidgetDialog'
import { QuickWidgetTypes } from '../../../../reducer/user.reducer'
import { makeSelectQuickModeEnabled } from '../../../../selectors/user'
import { selectOrdersHistory } from '../../../../selectors/orders'
import { Order, OrderStatus } from '../../../../proto/generated/order'
import OpenOrdersTable from '../../../ordersWidget/OpenOrdersTable'
import { OrdersNavButtonsDetailed } from '../../../postionsWidget'
import { fetchSecuritySnapshotsBulk } from '../../../../services/apis/candles'
import websocketActions from '../../../../actions/websocket.actions'
import { subscriptionTags } from '../../../../assets/constants'
import { groupIcebergBasketOrders, isIcebergGroup } from '../../../../util/icebergOrders'

const OrdersWidget: React.FC = () => {
    const ordersData = useSelector(selectOrdersHistory)
    const dispatch = useDispatch()
    const [showContent, setShowContent] = useState(false)
    const QuickEnableMode = useSelector(makeSelectQuickModeEnabled(QuickWidgetTypes.ORDER))

    const openOrders = useMemo(() => {
        const excludedStatuses = [
            OrderStatus.REJECTED,
            OrderStatus.CANCELLED,
            OrderStatus.EXECUTED,
            OrderStatus.FAILED,
        ]
        return ordersData?.filter((order) => order.status && !excludedStatuses.includes(order.status)) ?? []
    }, [ordersData])

    useEffect(() => {
        const timeoutId = setTimeout(() => setShowContent(true), 200)
        return () => clearTimeout(timeoutId)
    }, [])

    const securities = useMemo(() => {
        return groupIcebergBasketOrders(openOrders)
            .flatMap((orderOrGroup) => {
                if (isIcebergGroup(orderOrGroup)) {
                    return orderOrGroup.allOrders.map((order) => order.security)
                }
                return [orderOrGroup.security]
            })
            .filter(Boolean)
    }, [openOrders])

    useEffect(() => {
        if (!securities.length) return
        fetchSecuritySnapshotsBulk({ securities, dispatch })
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.ORDERS_WIDGET_SUBSCRIPTION_TAG,
                    securities,
                },
            ],
        })
    }, [securities, dispatch])

    return (
        <QuickWidgetDialog widgetType={QuickWidgetTypes.ORDER} title="Open Orders">
            {!showContent ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress size={20} />
                </Box>
            ) : openOrders.length === 0 ? (
                <Typography variant="body2" textAlign="center" mt={2} mb={2}>
                    No open orders.
                </Typography>
            ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <OpenOrdersTable
                        orders={openOrders}
                        tabType={OrdersNavButtonsDetailed.OPEN}
                        hideOrderStatus
                    />
                </Box>
            )}
        </QuickWidgetDialog>
    )
}

export default memo(OrdersWidget)
